import axios from "axios";
import React, { useEffect, useState, memo, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { Link, useParams } from "react-router-dom";

const ReplyForm = memo(function ReplyForm({
  commentId,
  userFullName,
  replyContent,
  onReplyChange,
  onSubmit,
  onCancel,
  submitting,
  error,
}) {
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSubmit(e, true, commentId);
    },
    [onSubmit, commentId],
  );

  return (
    <div className="ml-5 mb-3 mt-2">
      <div className="d-flex align-items-start">
        <div className="mr-2">
          <div
            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
            style={{ width: "32px", height: "32px", fontSize: "14px" }}
          >
            {userFullName?.substring(0, 2).toUpperCase() || "NN"}
          </div>
        </div>
        <div className="flex-grow-1">
          <form onSubmit={handleSubmit} className="d-flex flex-column">
            <textarea
              rows="2"
              className="form-control mb-2"
              placeholder="Nhập trả lời của bạn..."
              value={replyContent}
              onChange={onReplyChange}
              disabled={submitting}
            ></textarea>

            {error && (
              <div className="alert alert-danger py-1 px-2 mb-2" role="alert">
                <small>{error}</small>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={onCancel}
                disabled={submitting}
              >
                Hủy
              </button>
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={submitting}
              >
                {submitting ? "Đang gửi..." : "Trả lời"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

const CommentItem = memo(function CommentItem({
  comment,
  userId,
  userFullName,
  onReply,
  onDelete,
  replyingToComment,
  replyContent,
  handleReplyChange,
  handleCommentSubmit,
  cancelReply,
  submittingComment,
  replyFormError,
}) {
  // Memoize event handlers to prevent re-renders
  const handleReplyClick = useCallback(() => {
    onReply(comment);
  }, [onReply, comment]);

  const handleDeleteClick = useCallback(() => {
    onDelete(comment.id);
  }, [onDelete, comment.id]);

  const isCurrentUserComment = useMemo(
    () => userId === comment.userId.toString(),
    [userId, comment.userId],
  );

  const isReplyingToThisComment = useMemo(
    () => replyingToComment === comment.id,
    [replyingToComment, comment.id],
  );

  const formattedDate = useMemo(
    () => new Date(comment.createdAt).toLocaleDateString("vi-VN"),
    [comment.createdAt],
  );

  const avatarElement = useMemo(() => {
    if (comment.user?.avatar_url) {
      return (
        <img
          src={comment.user.avatar_url}
          alt={comment.user?.full_name}
          className="rounded-circle"
          style={{ width: "40px", height: "40px", objectFit: "cover" }}
        />
      );
    } else {
      return (
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
          style={{ width: "40px", height: "40px", fontSize: "18px" }}
        >
          {comment.user?.full_name?.substring(0, 2).toUpperCase() || "NN"}
        </div>
      );
    }
  }, [comment.user?.avatar_url, comment.user?.full_name]);

  const replyElements = useMemo(() => {
    if (!comment.replies || comment.replies.length === 0) return null;

    return (
      <div className="replies ml-4 pl-3 border-left mt-2">
        {comment?.replies.map((reply) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            userId={userId}
            userFullName={userFullName}
            onReply={onReply}
            onDelete={onDelete}
            replyingToComment={replyingToComment}
            replyContent={replyContent}
            handleReplyChange={handleReplyChange}
            handleCommentSubmit={handleCommentSubmit}
            cancelReply={cancelReply}
            submittingComment={submittingComment}
            replyFormError={replyFormError}
          />
        ))}
      </div>
    );
  }, [
    comment.replies,
    userId,
    userFullName,
    onReply,
    onDelete,
    replyingToComment,
    replyContent,
    handleReplyChange,
    handleCommentSubmit,
    cancelReply,
    submittingComment,
    replyFormError,
  ]);

  return (
    <div className="comment-item mb-3">
      <div
        className="media p-3 bg-white rounded"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
      >
        <div className="d-flex align-items-start">
          <div className="mr-3">{avatarElement}</div>
          <div className="media-body">
            <div className="mb-1">
              <h6 className="d-inline mb-0 font-weight-bold">
                {comment.user?.full_name}
              </h6>
              {comment.replyToUser && (
                <span className="text-muted ml-2">
                  <strong>@{comment.replyToUser?.full_name}</strong>
                </span>
              )}
            </div>
            <p className="mb-1">{comment.content}</p>
            <div className="d-flex align-items-center text-muted small">
              <span className="mr-3">{formattedDate}</span>
              {userId && (
                <>
                  <button
                    className="btn btn-sm btn-link p-0 text-muted mr-3"
                    onClick={handleReplyClick}
                  >
                    Trả lời
                  </button>
                  {isCurrentUserComment && (
                    <button
                      className="btn btn-sm btn-link p-0 text-danger"
                      onClick={handleDeleteClick}
                    >
                      Xóa
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {userId && isReplyingToThisComment && (
        <ReplyForm
          commentId={comment.id}
          userFullName={userFullName}
          replyContent={replyContent}
          onReplyChange={handleReplyChange}
          onSubmit={handleCommentSubmit}
          onCancel={cancelReply}
          submitting={submittingComment}
          error={replyFormError}
        />
      )}

      {replyElements}
    </div>
  );
});

const MainCommentForm = memo(function MainCommentForm({
  userFullName,
  commentContent,
  onCommentChange,
  onSubmit,
  submitting,
  deleting,
  error,
}) {
  return (
    <div className="main-comment-form mt-4 pt-3 border-top">
      <div className="d-flex align-items-start">
        <div className="mr-3">
          <div
            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px", fontSize: "18px" }}
          >
            {userFullName?.substring(0, 2).toUpperCase() || "NN"}
          </div>
        </div>
        <div className="flex-grow-1">
          <form onSubmit={(e) => onSubmit(e, false)}>
            <div className="form-group">
              <textarea
                className="form-control"
                rows="3"
                placeholder="Nhập bình luận của bạn..."
                value={commentContent}
                onChange={onCommentChange}
                disabled={submitting}
              ></textarea>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </div>
            )}

            <div className="form-group mb-0 text-right">
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={submitting || deleting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Đang gửi...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2"></i>
                    Gửi bình luận
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

const CommentsList = memo(function CommentsList({
  comments,
  userId,
  userFullName,
  onReply,
  onDelete,
  replyingToComment,
  replyContent,
  handleReplyChange,
  handleCommentSubmit,
  cancelReply,
  submittingComment,
  replyFormError,
}) {
  if (comments.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="far fa-comment-alt fa-3x text-muted mb-3"></i>
        <p>Chưa có bình luận nào cho bài viết này</p>
      </div>
    );
  }

  return (
    <div>
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          userId={userId}
          userFullName={userFullName}
          onReply={onReply}
          onDelete={onDelete}
          replyingToComment={replyingToComment}
          replyContent={replyContent}
          handleReplyChange={handleReplyChange}
          handleCommentSubmit={handleCommentSubmit}
          cancelReply={cancelReply}
          submittingComment={submittingComment}
          replyFormError={replyFormError}
        />
      ))}
    </div>
  );
});

// Related Post component
const RelatedPostItem = memo(function RelatedPostItem({ post }) {
  return (
    <div className="media mb-3 pb-3 border-bottom">
      {post.thumbnail && (
        <img
          src={post.thumbnail}
          alt={post.title}
          className="mr-3 rounded"
          style={{ width: "70px", height: "70px", objectFit: "cover" }}
        />
      )}
      <div className="media-body">
        <h6 className="mt-0 mb-1">
          <Link to={`/tin-tuc/${post.slug}`} className="text-dark">
            {post.title}
          </Link>
        </h6>
        <small className="text-muted">
          <i className="far fa-calendar-alt mr-1"></i>
          {new Date(post?.created_at).toLocaleDateString("vi-VN")}
        </small>
      </div>
    </div>
  );
});

export const PostDetail = () => {
  const [posts, setPosts] = useState({});
  const [comments, setComments] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [commentContent, setCommentContent] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [commentError, setCommentError] = useState("");
  const [replyFormError, setReplyFormError] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const { id } = useParams();
  const userId = localStorage.getItem("userId");
  const userFullName = localStorage.getItem("fullName");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    getData();
  }, [id]);

  const getData = useCallback(() => {
    axios
      .get("/post/show/" + id)
      .then((res) => {
        const postData = res.data.data;
        setPosts(postData);
        getComments(postData.id);

        if (postData?.topics?.length > 0) {
          getRelatedPosts(postData?.topics[0]?.id, postData.id);
        }
      })
      .catch((err) => console.log(err));
  }, [id]);

  const getRelatedPosts = useCallback((topicId, currentPostId) => {
    axios
      .get(`/post/public/shows?topicId=${topicId}&limit=6`)
      .then((res) => {
        const postsData = res.data.data?.posts || [];
        const related = postsData.filter((post) => post.id !== currentPostId);
        setRelatedPosts(related);
      })
      .catch((err) => console.log(err));
  }, []);

  const getComments = useCallback((postId) => {
    axios
      .get(`/comment/post/public/shows?ableId=${postId}`)
      .then((res) => {
        const commentData = res.data.data || [];
        setComments(commentData);
      })
      .catch((err) => console.log(err));
  }, []);

  const handleReplyChange = useCallback((e) => {
    setReplyContent(e.target.value);
    setReplyFormError("");
  }, []);

  const handleCommentContentChange = useCallback((e) => {
    setCommentContent(e.target.value);
    setCommentError("");
  }, []);

  const handleCommentSubmit = useCallback(
    (e, isReply = false) => {
      e.preventDefault();

      if (isReply) {
        setReplyFormError("");
      } else {
        setCommentError("");
      }

      if (!userId) {
        if (isReply) {
          setReplyFormError("Vui lòng đăng nhập để bình luận");
        } else {
          setCommentError("Vui lòng đăng nhập để bình luận");
        }
        return;
      }

      const content = isReply ? replyContent : commentContent;

      if (!content || content.trim().length < 10) {
        const errorMessage = "Nội dung bình luận phải có ít nhất 10 ký tự";
        if (isReply) {
          setReplyFormError(errorMessage);
        } else {
          setCommentError(errorMessage);
        }
        return;
      }

      const commentData = {
        ableId: posts?.id.toString(),
        parentId: isReply ? parentId : null,
        replyTo: isReply ? replyingTo : null,
        content: content.trim(),
      };

      setSubmittingComment(true);

      axios
        .post("/comment/post/create", commentData)
        .then(() => {
          toast.success(isReply ? "Đã trả lời bình luận" : "Đã thêm bình luận");
          if (isReply) {
            setReplyContent("");
            setReplyingTo(null);
            setParentId(null);
            setReplyingToComment(null);
          } else {
            setCommentContent("");
          }
          getComments(posts?.id);
        })
        .catch((err) => {
          console.error("Error submitting comment:", err);
          const errorMessage =
            err.response?.data?.messages?.content ||
            err.response?.data?.messages?.message ||
            "Không thể gửi bình luận. Vui lòng thử lại sau.";

          if (isReply) {
            setReplyFormError(errorMessage);
          } else {
            setCommentError(errorMessage);
          }
        })
        .finally(() => {
          setSubmittingComment(false);
        });
    },
    [
      userId,
      replyContent,
      commentContent,
      posts,
      parentId,
      replyingTo,
      getComments,
    ],
  );

  const handleReply = useCallback((comment) => {
    setReplyingToComment(comment.id);
    setParentId(comment.id);
    setReplyingTo(comment.userId);
    setReplyContent("");
    setReplyFormError("");
  }, []);

  const cancelReply = useCallback(() => {
    setParentId(null);
    setReplyingTo(null);
    setReplyingToComment(null);
    setReplyContent("");
    setReplyFormError("");
  }, []);

  const handleDeleteComment = useCallback(
    (commentId) => {
      setDeletingComment(true);

      axios
        .delete(`/comment/user/destroy/${commentId}`)
        .then(() => {
          toast.success("Đã xóa bình luận");
          getComments(posts?.id);
        })
        .catch((err) => {
          console.error("Error deleting comment:", err);
          toast.error(
            err.response?.data?.message ||
              "Không thể xóa bình luận. Vui lòng thử lại sau.",
          );
        })
        .finally(() => {
          setDeletingComment(false);
        });
    },
    [posts?.id, getComments],
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row px-xl-5">
          <div className="col-12">
            <nav className="breadcrumb bg-light mb-30">
              <Link to="/" className="breadcrumb-item text-dark">
                Trang chủ
              </Link>
              <span className="breadcrumb-item active">
                <Link
                  to={
                    posts?.topics?.length > 0
                      ? `/tin-tuc?t=${posts?.topics[0]?.slug}`
                      : "/tin-tuc"
                  }
                  className="text-dark"
                >
                  {posts?.topics?.length > 0
                    ? posts?.topics[0]?.name
                    : "Tin tức"}
                </Link>
              </span>
              <span className="breadcrumb-item active">{posts?.title}</span>
            </nav>
          </div>
        </div>
      </div>

      {posts ? (
        <div className="container" style={{ width: "100%" }}>
          <h1 className="text-uppercase text-center mb-4">
            {/* <h1 className="bg-secondary px-3 py-1 d-inline-block"> */}
            {posts?.title}
            {/* </h1> */}
          </h1>

          <div className="row">
            {/* Main content column - 8 of 12 on medium+ screens */}
            <div className="col-lg-8">
              <div
                className="mx-auto px-3 px-md-4 post-content"
                dangerouslySetInnerHTML={{ __html: posts?.content }}
              ></div>

              <div className="mt-5 px-3 px-md-4">
                <div className="card shadow-sm">
                  <div className="card-header bg-primary ">
                    <h5 className="mb-0 text-white">
                      <i className="fas fa-comments mr-2 text-white"></i>
                      Bình luận ({comments.length})
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="comments-container mb-4">
                      <CommentsList
                        comments={comments}
                        userId={userId}
                        userFullName={userFullName}
                        onReply={handleReply}
                        onDelete={handleDeleteComment}
                        replyingToComment={replyingToComment}
                        replyContent={replyContent}
                        handleReplyChange={handleReplyChange}
                        handleCommentSubmit={handleCommentSubmit}
                        cancelReply={cancelReply}
                        submittingComment={submittingComment}
                        replyFormError={replyFormError}
                      />
                    </div>

                    {userId ? (
                      <MainCommentForm
                        userFullName={userFullName}
                        commentContent={commentContent}
                        onCommentChange={handleCommentContentChange}
                        onSubmit={handleCommentSubmit}
                        submitting={submittingComment}
                        deleting={deletingComment}
                        error={commentError}
                      />
                    ) : (
                      <div
                        className="alert alert-warning d-flex align-items-center mb-0 mt-4"
                        role="alert"
                      >
                        <i className="fas fa-exclamation-circle mr-2"></i>
                        <div>
                          Vui lòng{" "}
                          <Link to="/login" className="alert-link">
                            đăng nhập
                          </Link>{" "}
                          để bình luận
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar column - 4 of 12 on medium+ screens, hidden on smaller screens */}
            <div className="col-lg-4 d-none d-lg-block">
              <div className="position-sticky" style={{ top: "20px" }}>
                <div className="card shadow-sm mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0 text-white">
                      <i className="fas fa-newspaper mr-2"></i>
                      Bài viết liên quan
                    </h5>
                  </div>
                  <div className="card-body">
                    {relatedPosts?.length > 0 ? (
                      <div className="related-posts">
                        {relatedPosts.map((post) => (
                          <RelatedPostItem key={post.id} post={post} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center mb-0">
                        Không có bài viết liên quan
                      </p>
                    )}
                  </div>
                </div>

                {/* You can add more sidebar widgets here if needed */}
                <div className="card shadow-sm">
                  <div className="card-header bg-secondary text-dark">
                    <h5 className="mb-0">
                      <i className="fas fa-tags mr-2"></i>
                      Chủ đề
                    </h5>
                  </div>
                  <div className="card-body">
                    {posts?.topics?.map((topic, index) => (
                      <Link
                        key={index}
                        to={`/tin-tuc?t=${topic.slug}`}
                        className="btn btn-light mr-2 mb-2"
                      >
                        {topic.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-center " style={{ minHeight: "200px" }}>
          Không tìm thấy bài viết
        </p>
      )}
    </>
  );
};
